import {action, computed, observable} from 'mobx';
import {successAlert} from 'shared/tools';
import {t} from 'shared/core';
import {endpoints, types} from 'main/tools';
import Signature from 'main/stores/signatures/Signature';
import {CompanyDocument, NotificationRecipient, AssignmentRule} from 'main/stores/documents';
import {CustomField, CustomFieldGroup} from 'main/stores/custom_fields';
import Folder from 'main/stores/folders/Folder';
import {DomainStore} from 'shared/store';
import _ from 'lodash';
import {User} from 'main/stores/users';
import {Employee} from 'main/stores/employees';
import MERGE_FIELDS from 'main/stores/documents/mergeFields';

class CompanyDocumentEditState {
  store = new DomainStore();
  history;
  match;

  @observable companyDocument;
  @observable signatures = [];
  @observable errors = {};
  @observable folders = [];
  @observable users = [];
  @observable employees = [];

  receiveProps({history, match}) {
    this.match = match;
    this.history = history;
  }

  @action async load() {
    await this.store._compose([
      endpoints.COMPANY_DOCUMENT.with(this.match.params.id),
      endpoints.COMPANY_SIGNATURES,
      endpoints.FOLDERS.ALL,
      endpoints.CUSTOM_FIELD_GROUPS.ALL,
      endpoints.USERS.ACTIVE,
      endpoints.EMPLOYEES.ALL
    ]);

    this.companyDocument = new CompanyDocument(this.store._getSingle(types.COMPANY_DOCUMENT));
    this.signatures = this.store._getAll(types.COMPANY_SIGNATURE, Signature);
    this.folders = this.store._getAll(types.FOLDER, Folder);
    this.customFields = this.store._getAll(types.CUSTOM_FIELD, CustomField);
    this.customFieldGroups = this.store._getAll(types.CUSTOM_FIELD_GROUP, CustomFieldGroup);
    this.users = this.store._getAll(types.USER, User);
    this.employees = this.store._getAll(types.EMPLOYEE, Employee);
  }

  @action updateContent(content) {
    this.companyDocument.richText = content.html;
    this.companyDocument.lexicalState = content.state;
  }

  @action goBack(saved = false) {
    this.history.push(`/documents${saved && this.companyDocument.folderId ? `?ex=${this.companyDocument.folderId}` : ''}`);
  }

  @action async saveCompanyDocument() {
    const {model, errors} = await this.store.patch(this.companyDocument);
    this.errors = errors;

    if (model) {
      successAlert(t('company_settings.documents.alerts.Company document saved.'));
      this.goBack(true);
    }
  }

  @action cancelCompanyDocumentEdit() {
    this.goBack();
  }

  @action async saveRichTextTemplate() {
    return this.saveCompanyDocument();
  }

  @action async savePdfTemplate(annotations) {
    this.companyDocument.annotations = annotations;
    await this.saveCompanyDocument();
  }

  @computed get mergeFields() {
    const customFieldMergeFields = _.chain(this.customFields)
      .filter('employeeCanView')
      .sortBy('name')
      .groupBy('customFieldGroupId')
      .map((customFields, customFieldGroupId) => {
        return {
          name: _.find(this.customFieldGroups, {id: customFieldGroupId}).name,
          alreadyTranslated: true,
          options: _.map(customFields, customField => {
            return {name: customField.name, key: `custom_field_${customField.id}`, placeholder: `#[${customField.name}]`, alreadyTranslated: true};
          })
        };
      })
      .value();

    return MERGE_FIELDS.concat(customFieldMergeFields);
  }

  @computed get signatureList() {
    return [Signature.employeeSignature(), ...this.signatures];
  }

  @computed get sortedFolders() {
    return _.orderBy(this.folders, 'order');
  }

  @computed get recipientOptions() {
    return [
      new NotificationRecipient({
        recipientType: 'manager'
      }),
      ...this.users.map(user => new NotificationRecipient(
        {
          recipientType: 'specific_user',
          user: user
        }
      ))
    ]
  }

  @computed get ruleOptions() {
    const ruleOptions = [];
    const order = this.companyDocument.assignmentRules.length;

    if (!_.some(this.companyDocument.assignmentRules, {assigneeType: 'manager'})) {
      ruleOptions.push(new AssignmentRule({
        assigneeType: 'manager',
        order
      }));
    }

    const existingIds = _.map(this.companyDocument.assignmentRules, 'employee.id');
    const availableEmployees = _.filter(this.employees, (employee) => !_.includes(existingIds, employee.id));

    ruleOptions.push(...availableEmployees.map((employee) => new AssignmentRule(
      {
        assigneeType: 'specific_employee',
        employee,
        order
      }
    )));

    return ruleOptions;
  }
}

export default CompanyDocumentEditState;
